import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  sendAnalysisQuery,
  stopAnalysisGeneration,
} from '@/redux/asyncThunks/gameAnalysis'

const ChatInput: React.FC = () => {
  const [textInput, setTextInput] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user.user)
  const { isGenerating, isConnected } = useAppSelector(
    (state) => state.gameAnalysis
  )

  const canSendMessage = useMemo(
    () => !isGenerating && isConnected && !!textInput.trim() && !!user,
    [isGenerating, isConnected, textInput, user]
  )

  const placeholder = useMemo(() => {
    if (!isConnected) return 'Connecting...'
    if (isGenerating) return 'Generating response...'
    return 'Type your message...'
  }, [isConnected, isGenerating])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTextInput(e.target.value)
    },
    []
  )

  const handleSendMessage = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (canSendMessage && user) {
        dispatch(sendAnalysisQuery({ message: textInput, user: user.email }))
        setTextInput('')
      }
    },
    [canSendMessage, dispatch, textInput, user]
  )

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (canSendMessage && user) {
          dispatch(sendAnalysisQuery({ message: textInput, user: user.email }))
          setTextInput('')
        }
      }
    },
    [canSendMessage, dispatch, textInput, user]
  )

  const handleStopStreaming = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      dispatch(stopAnalysisGeneration())
    },
    [dispatch]
  )

  return (
    <form onSubmit={handleSendMessage} className='flex flex-1 gap-2'>
      <div className='relative min-h-[50px] flex items-start flex-1 px-3 py-2 border border-[#CBD5E1] bg-[#E6ECF2] rounded-2xl'>
        <div className='flex flex-wrap items-center gap-1 w-full'>
          {/* Text Input */}
          <Textarea
            ref={inputRef}
            value={textInput}
            disabled={!isConnected || isGenerating}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            rows={1}
            className='flex-1 min-w-[100px] min-h-[36px] max-h-40 resize-none overflow-y-auto text-sm  leading-[36px] bg-transparent border-none shadow-none focus:outline-none focus:ring-0 focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:ring-0 appearance-none'
          />
        </div>

        {/* Send/Stop Button */}
        <div className='flex items-center ml-2'>
          {isGenerating ? (
            <Button
              className='bg-transparent shadow-none hover:bg-transparent p-1'
              onClick={handleStopStreaming}
              type='button'
              size='icon'
            >
              <img
                src={'/icons/stop-circle.svg'}
                alt='Stop'
                className='h-7 w-7'
              />
            </Button>
          ) : (
            <Button
              className='bg-transparent shadow-none hover:bg-transparent p-1'
              disabled={!canSendMessage}
              type='submit'
              size='icon'
            >
              <img
                src={
                  !textInput.trim()
                    ? '/icons/arrow-up-circle-white.svg'
                    : '/icons/arrow-up-circle.svg'
                }
                alt='Send'
                className='h-7 w-7'
              />
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}

export default ChatInput
